import { t } from "i18next";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isMobile } from "react-device-detect";
import Skeleton from "react-loading-skeleton";
import { Carousel } from "react-responsive-carousel";
import images from "../../../resources/images";
import { getCurrentLocale } from "../../../utils/utilFunctions";
import classes from "./EntityPictures.module.css";



const EntityPictures = ({ coverImages, onImageClick, isLoading, variant = "grid" }) => {
  const [imageDimensions, setImageDimensions] = useState([]);
  const getGridStyle = () => {
    switch (coverImages.length) {
      case 1:
        return classes.grid1;
      case 2:
        return classes.grid2;
      case 3:
        return classes.grid3;
      case 4:
        return classes.grid4;
      case 5:
        return classes.grid5;

    }

    if (coverImages.length > 5) return classes.grid5
  };

  const getSelectedItemByLocale = () => {
    const locale = getCurrentLocale();
    if (locale === "he") {
      return coverImages.length - 1;
    } else {
      return 0;
    }
  };

  const getCoverImagesByLocale = () => {
    const locale = getCurrentLocale();
    if (locale === "he") {
      return [...coverImages].reverse();
    } else {
      return coverImages;
    }
  };

  const setImageSize = (index, image) => {
    const img = new Image()
    img.src = "https://vf-uk-images.s3.eu-central-1.amazonaws.com/03_683b25e3c1.jpg"


    const dimensions = { width: img.width, height: img.height };
    setImageDimensions(prevDims => {
      const newDims = [...prevDims];
      newDims[index] = dimensions;
      return newDims;
    });
  };


  const isAspectRatioClose = (width, height) => {
    const ratio = width / height;
    const squareRatio = 1; // Aspect ratio for 1:1
    const fourThreeRatio = 4 / 3; // Aspect ratio for 4:3

    // Decide the threshold for how close the ratio needs to be
    const threshold = 0.35; // 5% tolerance for example
    console.log('ratio check:', Math.abs(ratio - squareRatio) <= threshold ||
      Math.abs(ratio - fourThreeRatio) <= threshold)
    // Check if the aspect ratio is close to 1:1 or 4:3 within the threshold
    return (
      Math.abs(ratio - squareRatio) <= threshold ||
      Math.abs(ratio - fourThreeRatio) <= threshold
    );
  };

  // ── Flickity-inspired carousel state (used only when variant === "carousel") ──
  const trackRef = useRef(null);
  const [pageCount, setPageCount] = useState(1);
  const [selectedPage, setSelectedPage] = useState(0);

  const computePageInfo = useCallback(() => {
    const track = trackRef.current;
    if (!track) return { count: 1, current: 0 };
    const pageWidth = Math.max(1, track.clientWidth);
    // Use the actual number of images instead of calculating from scrollWidth
    // to avoid fractional pages caused by gaps between items
    const count = Math.max(1, coverImages.length);
    const current = Math.round(track.scrollLeft / pageWidth);
    return { count, current: Math.min(current, count - 1) };
  }, [coverImages.length]);

  const refreshPageInfo = useCallback(() => {
    const { count, current } = computePageInfo();
    setPageCount(count);
    setSelectedPage(current);
  }, [computePageInfo]);

  useLayoutEffect(() => {
    if (variant === "carousel") refreshPageInfo();
  }, [variant, refreshPageInfo, coverImages]);

  useEffect(() => {
    if (variant !== "carousel") return;
    const onResize = () => refreshPageInfo();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [variant, refreshPageInfo]);

  const scrollToPage = (page) => {
    const track = trackRef.current;
    if (!track) return;
    // Loop infinitely: wrap around to the first page after the last, and vice versa
    const wrappedPage = ((page % pageCount) + pageCount) % pageCount;
    track.scrollTo({ left: wrappedPage * track.clientWidth, behavior: "smooth" });
  };

  const dragState = useRef({ isDown: false, startX: 0, startScroll: 0, moved: false });

  const onMouseDown = (e) => {
    const track = trackRef.current;
    if (!track) return;
    dragState.current = { isDown: true, startX: e.pageX, startScroll: track.scrollLeft, moved: false };
    track.classList.add(classes.picsDragging);
  };

  const endDrag = () => {
    const track = trackRef.current;
    if (!track) return;
    if (dragState.current.isDown) {
      dragState.current.isDown = false;
      track.classList.remove(classes.picsDragging);
    }
  };

  const onMouseMove = (e) => {
    const track = trackRef.current;
    if (!track || !dragState.current.isDown) return;
    const dx = e.pageX - dragState.current.startX;
    if (Math.abs(dx) > 3) dragState.current.moved = true;
    track.scrollLeft = dragState.current.startScroll - dx;
  };

  const onClickCapture = (e) => {
    if (dragState.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragState.current.moved = false;
    }
  };

  if (variant === "carousel") {
    if (isLoading) return <Skeleton className={classes.cover} />;

    if (!coverImages || coverImages.length === 0) {
      return (
        <div className={classes.noImageContainer}>
          <img className={classes.defaultImage} src={images.veganFriendlyWhite} alt="" />
          <h4 className={classes.defaultImageText}>
            {t("no_image_place_holder_title")}
            <br />
            <b>{t("no_image_place_holder_subtitle")}</b>
          </h4>
        </div>
      );
    }

    const isRTL = getCurrentLocale() === "he";

    return (
      <div className={classes.picsCarouselWrapper}>
        <div className={classes.picsCarousel}>
          <button
            type="button"
            className={`${classes.picsNavButton} ${classes.picsNavPrev}`}
            onClick={() => scrollToPage(selectedPage - 1)}
            aria-label="Previous image"
          >
            {/* In RTL the browser mirrors ‹↔›; supply the opposite glyph so it renders outward */}
            <span aria-hidden="true">{isRTL ? "›" : "‹"}</span>
          </button>

          <div
            ref={trackRef}
            className={classes.picsTrack}
            onScroll={refreshPageInfo}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onClickCapture={onClickCapture}
            role="region"
            aria-roledescription="carousel"
          >
            {coverImages.map((cover, index) => (
              <div
                key={index}
                className={classes.picsCell}
                onClick={() => onImageClick(index)}
              >
                {cover?.is_illustration && (
                  <div className={classes.aiLabel}>AI Image</div>
                )}
                <img
                  className={classes.picsCellImage}
                  src={cover?.url ?? cover}
                  alt="cover photo"
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            className={`${classes.picsNavButton} ${classes.picsNavNext}`}
            onClick={() => scrollToPage(selectedPage + 1)}
            aria-label="Next image"
          >
            {/* In RTL the browser mirrors ‹↔›; supply the opposite glyph so it renders outward */}
            <span aria-hidden="true">{isRTL ? "‹" : "›"}</span>
          </button>
        </div>

        {pageCount > 1 && (
          <ol className={classes.picsPageDots}>
            {Array.from({ length: pageCount }).map((_, i) => (
              <li key={i}>
                <button
                  type="button"
                  className={`${classes.picsPageDot} ${i === selectedPage ? classes.picsPageDotSelected : ""}`}
                  onClick={() => scrollToPage(i)}
                  aria-label={`Go to image ${i + 1}`}
                  aria-current={i === selectedPage ? "true" : undefined}
                />
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  }


  return isLoading ? (
    <Skeleton className={classes.cover} />
  ) : coverImages && coverImages.length > 0 ? (
    isMobile ? (
      <Carousel
        className={classes.imagesContainer}
        autoPlay={true}
        showArrows={true}
        showStatus={false}
        showThumbs={false}
        dynamicHeight={true}
        onClickItem={() => onImageClick(0)}
        selectedItem={getSelectedItemByLocale()}
      >
        {getCoverImagesByLocale().map((cover, index) => (
          <img key={index} className={classes.cover} src={cover?.url ?? cover} alt="cover photo" />
        ))}
      </Carousel>
    ) : (
      <div className={getGridStyle()}>
        {coverImages.slice(0, 5).map((cover, index) => {

          return (
            <div key={index} className={classes.gridItem} onClick={() => onImageClick(index)}>
              {cover?.is_illustration && (

                <div
                  style={{
                    position: 'absolute',
                    bottom: '10px',
                    right: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    padding: '5px 10px',
                    fontSize: '12px',
                    borderRadius: '200px',
                  }}
                >
                  AI Image
                </div>
              )}
              <img onLoad={() => { setImageSize(index, cover?.url ?? cover) }} className={classes.images} style={{
                objectPosition: imageDimensions[index] &&
                  (
                    imageDimensions[index].width &&
                    imageDimensions[index].height &&
                    (
                      isAspectRatioClose(imageDimensions[index].width, imageDimensions[index].height)
                    )
                  )
                  ? "top"
                  : "50% 50%"

              }} src={cover?.url ?? cover} alt="cover photo" />
              <div className={classes.showMoreImagesContainer}>
                <p className={classes.showMoreText}>+{coverImages.length - 5}</p>
              </div>
            </div>
          )
        })}
      </div>


    )
  ) : (
    <div className={classes.noImageContainer}>
      <img className={classes.defaultImage} src={images.veganFriendlyWhite} />
      <h4 className={classes.defaultImageText}>
        {t("no_image_place_holder_title")}
        <br />
        <b>{t("no_image_place_holder_subtitle")}</b>
      </h4>
    </div>
  );
};

export default EntityPictures;